import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';
import defaultAvatar from '../assets/svg/images/default-avatar.jpg';
import { FaArrowLeft, FaStar, FaClock, FaLanguage, FaGlobe, FaFilm, FaUserTie, FaUsers, FaPenAlt, FaBoxOpen } from 'react-icons/fa';

const styles = `
  .about-movie-container {
    min-height: 100vh;
    background: #000;
    color: #fff;
    padding-bottom: 2rem;
  }

  .movie-header {
    position: relative;
    height: 60vh;
    overflow: hidden;
  }

  .movie-backdrop {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    filter: brightness(0.4);
  }

  .movie-header-content {
    position: relative;
    z-index: 1;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding: 2rem;
  }

  .back-button {
    position: absolute;
    top: 2rem;
    left: 2rem;
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 2;
  }

  .movie-poster {
    width: 200px;
    height: 300px;
    border-radius: 8px;
    object-fit: cover;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
    margin-bottom: 1rem;
  }

  .movie-title {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    color: #fff;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  }

  .movie-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1rem;
    color: #ccc;
  }

  .movie-meta-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .movie-actions {
    display: flex;
    gap: 1rem;
    margin-top: 1rem;
  }

  .action-button {
    padding: 0.5rem 1.5rem;
    border-radius: 9999px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .play-button {
    background: #FF1493;
    color: white;
    border: none;
  }

  .play-button:hover {
    background: #e01385;
  }

  .trailer-button {
    background: transparent;
    color: white;
    border: 2px solid white;
  }

  .trailer-button:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  .movie-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem;
    display: grid;
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  @media (min-width: 1024px) {
    .movie-content {
      grid-template-columns: 300px 1fr;
    }
  }

  .movie-sidebar {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  .movie-main {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  .section-title {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: #FF1493;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .movie-description {
    line-height: 1.6;
    color: #ddd;
  }

  .detail-card {
    background: #1a1a1a;
    border-radius: 8px;
    padding: 1.5rem;
  }

  .detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 1rem;
  }

  .detail-item {
    margin-bottom: 1rem;
  }

  .detail-label {
    font-size: 0.875rem;
    color: #999;
    margin-bottom: 0.25rem;
  }

  .detail-value {
    font-size: 1rem;
    color: #fff;
  }

  .actor-card {
    display: flex;
    align-items: center;
    gap: 1rem;
    background: #1a1a1a;
    border-radius: 8px;
    padding: 1rem;
  }

  .actor-image {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    object-fit: cover;
  }

  .actor-name {
    font-weight: 600;
  }

  .actor-role {
    font-size: 0.875rem;
    color: #999;
  }

  .cast-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 1rem;
  }

  .crew-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .crew-item {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem 0;
    border-bottom: 1px solid #333;
  }

  .crew-role {
    color: #999;
  }

  .crew-name {
    color: #fff;
  }
`;

const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default function AboutMovie() {
  const { movieId } = useParams();
  const navigate = useNavigate();
  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchMovie() {
      try {
        const movieDoc = doc(db, 'movies', movieId);
        const movieSnap = await getDoc(movieDoc);
        
        if (movieSnap.exists()) {
          setMovie({ id: movieSnap.id, ...movieSnap.data() });
        } else {
          toast.error("Movie not found");
          navigate('/');
        }
      } catch (error) {
        toast.error("Failed to load movie details");
        console.error("Error fetching movie:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchMovie();
  }, [movieId, navigate]);

  if (loading || !movie) {
    return <Spinner />;
  }

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const parseList = (str) => {
    if (!str) return [];
    return str.split(',').map(item => item.trim());
  };

  return (
    <div className="about-movie-container">
      {/* Movie Header with Backdrop */}
      <div className="movie-header">
        <div 
          className="movie-backdrop"
          style={{ backgroundImage: `url(${movie.posterUrl})` }}
        ></div>
        
        <button 
          className="back-button"
          onClick={() => navigate(-1)}
        >
          <FaArrowLeft />
        </button>
        
        <div className="movie-header-content">
          <div className="flex flex-col md:flex-row gap-8 items-start md:items-end">
            <img 
              src={movie.posterUrl} 
              alt={movie.title}
              className="movie-poster hidden md:block"
            />
            
            <div>
              <h1 className="movie-title">{movie.title}</h1>
              
              <div className="movie-meta">
                <div className="movie-meta-item">
                  <FaStar className="text-yellow-400" />
                  <span>{movie.releaseYear}</span>
                </div>
                
                <div className="movie-meta-item">
                  <FaClock />
                  <span>{formatDuration(parseInt(movie.duration))}</span>
                </div>
                
                <div className="movie-meta-item">
                  <FaLanguage />
                  <span>{movie.language}</span>
                </div>
                
                <div className="movie-meta-item">
                  <FaGlobe />
                  <span>{movie.country}</span>
                </div>
                
                <div className="movie-meta-item">
                  <FaFilm />
                  <span>{movie.genre}</span>
                </div>
                
                <div className="movie-meta-item">
                  <span className="px-2 py-1 bg-gray-800 rounded text-sm">
                    {movie.contentRating}
                  </span>
                </div>
              </div>
              
              <div className="movie-actions">
                <button 
                  className="action-button play-button"
                  onClick={() => navigate(`/movie/${movie.id}`)}
                >
                  Play Movie
                </button>
                
                {movie.trailerUrl && (
                  <button 
                    className="action-button trailer-button"
                    onClick={() => window.open(movie.trailerUrl, '_blank')}
                  >
                    Watch Trailer
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Movie Content */}
      <div className="movie-content">
        {/* Sidebar */}
        <div className="movie-sidebar">
          <div className="detail-card">
            <h2 className="section-title">
              <FaBoxOpen />
              Details
            </h2>
            
            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-label">Status</div>
                <div className="detail-value">{movie.status}</div>
              </div>
              
              <div className="detail-item">
                <div className="detail-label">Resolution</div>
                <div className="detail-value">{movie.resolution}</div>
              </div>
              
              <div className="detail-item">
                <div className="detail-label">Featured</div>
                <div className="detail-value">
                  {movie.isFeatured ? 'Yes' : 'No'}
                </div>
              </div>
            </div>
          </div>
          
          {/* Main Actor */}
          {movie.mainActorName && (
            <div className="detail-card">
              <h2 className="section-title">
                <FaUserTie />
                Main Actor
              </h2>
              
              <div className="actor-card">
                <img 
                  src={movie.actorImageUrl || defaultAvatar} 
                  alt={movie.mainActorName}
                  className="actor-image"
                />
                <div>
                  <div className="actor-name">{movie.mainActorName}</div>
                  <div className="actor-role">Lead Actor</div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Main Content */}
        <div className="movie-main">
          {/* Description */}
          <div className="detail-card">
            <h2 className="section-title">Synopsis</h2>
            <p className="movie-description">
              {movie.description || 'No description available.'}
            </p>
          </div>
          
          {/* Cast */}
          {movie.cast && (
            <div className="detail-card">
              <h2 className="section-title">
                <FaUsers />
                Cast
              </h2>
              
              <div className="cast-grid">
                {parseList(movie.cast).map((actor, index) => (
                  <div key={index} className="actor-card">
                    <img 
                      src={defaultAvatar} 
                      alt={actor}
                      className="actor-image"
                    />
                    <div>
                      <div className="actor-name">{actor}</div>
                      <div className="actor-role">Actor</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Crew */}
          <div className="detail-card">
            <h2 className="section-title">
              <FaPenAlt />
              Crew
            </h2>
            
            <div className="crew-list">
              {movie.directors && (
                <div className="crew-item">
                  <span className="crew-role">Directors:</span>
                  <span className="crew-name">{movie.directors}</span>
                </div>
              )}
              
              {movie.writers && (
                <div className="crew-item">
                  <span className="crew-role">Writers:</span>
                  <span className="crew-name">{movie.writers}</span>
                </div>
              )}
              
              {movie.producers && (
                <div className="crew-item">
                  <span className="crew-role">Producers:</span>
                  <span className="crew-name">{movie.producers}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}